import { type API_UserOrganization, Role } from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Building2, ShieldCheck, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	fetchUserOrganizationsQueryOptions,
	useCreateOrganization,
	useDeleteOrganization,
} from "@/api/organizations";
import { Badge } from "@/components/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useApiParameters } from "@/hooks/useApiParameters";
import { getOrganizationRoleLabel } from "@/utils/roleUtils";
import { AppBaseLayout } from "./_app/-components/AppBaseLayout";
import { OnboardingModal } from "./-components/OnboardingModal";

const searchSchema = z.object({
	skipAutoRedirect: z.boolean().catch(false),
});

export const Route = createFileRoute("/_auth/app/select-organization")({
	validateSearch: searchSchema,
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		context.queryClient.ensureQueryData(
			fetchUserOrganizationsQueryOptions(userId, token),
		);
		return { userId, token };
	},
	component: SelectOrgPage,
});

function SelectOrgPage() {
	const { userId, token } = Route.useLoaderData();
	const { skipAutoRedirect } = Route.useSearch();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: organizations } = useSuspenseQuery(
		fetchUserOrganizationsQueryOptions(userId, token),
	);

	const ownedOrgs = useMemo(
		() => organizations.filter((o) => o.role === Role.ORG_OWNER),
		[organizations],
	);
	const invitedOrgs = useMemo(
		() => organizations.filter((o) => o.role !== Role.ORG_OWNER),
		[organizations],
	);

	// Auto-redirect to the only organization if user has exactly one and skipAutoRedirect is false
	useEffect(() => {
		if (!skipAutoRedirect && organizations.length === 1) {
			navigate({
				to: "/app/org/$orgId",
				params: { orgId: organizations[0].id },
				replace: true,
			});
		}
	}, [skipAutoRedirect, organizations, navigate]);

	// Create Organization Dialog state
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [newOrgName, setNewOrgName] = useState("");
	const [newOrgDescription, setNewOrgDescription] = useState("");
	const createOrganizationMutation = useCreateOrganization();
	const deleteOrganizationMutation = useDeleteOrganization();
	const [orgToDelete, setOrgToDelete] = useState<API_UserOrganization | null>(
		null,
	);
	const [deleteOrgNameConfirm, setDeleteOrgNameConfirm] = useState("");

	// Onboarding Modal state
	const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
	const [createdOrg, setCreatedOrg] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const handleCreateOrganization = async () => {
		if (!newOrgName.trim()) return;
		try {
			const result = await createOrganizationMutation.mutateAsync({
				token,
				data: {
					name: newOrgName.trim(),
					description: newOrgDescription.trim() || undefined,
				},
			});
			// Store created org info for onboarding
			setCreatedOrg({ id: result.id, name: result.name });
			// Close create dialog and open onboarding
			setIsCreateDialogOpen(false);
			setNewOrgName("");
			setNewOrgDescription("");
			setIsOnboardingOpen(true);
		} catch (error) {
			console.error("Failed to create organization:", error);
		}
	};

	const handleOpenDeleteDialog = (org: API_UserOrganization) => {
		setOrgToDelete(org);
		setDeleteOrgNameConfirm("");
	};

	const handleCloseDeleteDialog = () => {
		setOrgToDelete(null);
		setDeleteOrgNameConfirm("");
	};

	const handleConfirmDeleteOrganization = async () => {
		if (!orgToDelete) return;
		try {
			await deleteOrganizationMutation.mutateAsync({
				token,
				organizationId: orgToDelete.id,
			});
			setOrgToDelete(null);
			setDeleteOrgNameConfirm("");
		} catch (error) {
			// Error feedback is handled by toast system
			console.error("Failed to delete organization:", error);
		}
	};

	// No special loader state needed here since loader ensures token/userId
	// Still, show a small spinner while organizations query resolves (Suspense covers this in parent if used)
	if (!organizations) {
		return (
			<div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
				<div className="loading loading-spinner loading-lg" />
			</div>
		);
	}

	return (
		<AppBaseLayout>
			<div className="min-h-screen bg-base-100 py-6 sm:py-12 px-3 sm:px-4">
				<div className="max-w-5xl mx-auto space-y-6 sm:space-y-8">
					<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
						<div className="space-y-1 sm:space-y-2">
							<h1
								data-testid="page-title"
								className="text-xl sm:text-3xl font-bold text-base-content"
							>
								{t("selectOrganization.title")}
							</h1>
							<p className="text-sm sm:text-base text-base-content/70">
								{t("selectOrganization.subtitle")}
							</p>
						</div>
						<Dialog
							open={isCreateDialogOpen}
							onOpenChange={setIsCreateDialogOpen}
						>
							<DialogTrigger asChild>
								<Button className="btn btn-primary text-primary-content w-full sm:w-auto">
									{t("organization.createNew")}
								</Button>
							</DialogTrigger>
							<DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto mx-2 sm:mx-auto">
								<DialogHeader className="space-y-3 pb-6 border-b border-base-300">
									<DialogTitle className="text-xl font-semibold text-base-content flex items-center gap-2">
										<Building2 className="h-5 w-5 text-primary" />
										{t("organization.createNew")}
									</DialogTitle>
									<DialogDescription className="text-base-content/70">
										{t("organization.createDescription")}
									</DialogDescription>
								</DialogHeader>
								<div className="space-y-6 py-6">
									<div className="grid gap-6">
										<div className="grid gap-2">
											<Label htmlFor="org-name">{t("organization.name")}</Label>
											<Input
												id="org-name"
												value={newOrgName}
												onChange={(e) => setNewOrgName(e.target.value)}
												placeholder={t("organization.namePlaceholder")}
												maxLength={100}
											/>
										</div>
										<div className="grid gap-2">
											<Label htmlFor="org-description">
												{t("organization.description")}
											</Label>
											<Textarea
												id="org-description"
												value={newOrgDescription}
												onChange={(e) => setNewOrgDescription(e.target.value)}
												placeholder={t("organization.descriptionPlaceholder")}
												maxLength={500}
												rows={3}
												className="min-h-[80px] resize-none"
											/>
										</div>
									</div>
								</div>
								<DialogFooter>
									<button
										type="button"
										className="btn btn-outline"
										onClick={() => setIsCreateDialogOpen(false)}
									>
										{t("common.cancel")}
									</button>
									<button
										type="button"
										className="btn btn-primary"
										onClick={handleCreateOrganization}
										disabled={
											!newOrgName.trim() || createOrganizationMutation.isPending
										}
									>
										{createOrganizationMutation.isPending
											? t("common.creating")
											: t("common.create")}
									</button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					</div>

					{organizations.length === 0 ? (
						<div className="mt-4 sm:mt-6 rounded-lg border border-dashed p-6 sm:p-10 text-center">
							<h3 className="text-base sm:text-lg font-semibold text-base-content mb-2">
								{t("organization.noOrganizations")}
							</h3>
							<p className="text-sm sm:text-base text-base-content/70 mb-4 sm:mb-6">
								{t("organization.noOrganizationsDescription")}
							</p>
							<Button
								className="btn btn-primary text-primary-content w-full sm:w-auto"
								onClick={() => setIsCreateDialogOpen(true)}
							>
								{t("organization.createFirst")}
							</Button>
						</div>
					) : (
						<div className="space-y-6 sm:space-y-10 mt-4 sm:mt-6">
							<Section
								title={t("selectOrganization.ownedTitle")}
								items={ownedOrgs}
								emptyText={t("selectOrganization.emptyOwned")}
								testId="owned-organizations-section"
								onRequestDelete={handleOpenDeleteDialog}
							/>
							<Section
								title={t("selectOrganization.invitedTitle")}
								items={invitedOrgs}
								emptyText={t("selectOrganization.emptyInvited")}
								testId="invited-organizations-section"
								onRequestDelete={handleOpenDeleteDialog}
							/>
						</div>
					)}
				</div>
			</div>

			{/* Onboarding Modal - shown after organization creation */}
			{createdOrg && (
				<OnboardingModal
					isOpen={isOnboardingOpen}
					onClose={() => {
						setIsOnboardingOpen(false);
						setCreatedOrg(null);
					}}
					organizationName={createdOrg.name}
					organizationId={createdOrg.id}
				/>
			)}

			{/* Delete Organization Confirmation Dialog */}
			<Dialog
				open={!!orgToDelete}
				onOpenChange={(open) => {
					if (!open) {
						handleCloseDeleteDialog();
					}
				}}
			>
				<DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2 text-error">
							<AlertTriangle className="h-5 w-5" />
							{t("selectOrganization.deleteDialog.title")}
						</DialogTitle>
						<DialogDescription>
							{t("selectOrganization.deleteDialog.description", {
								organizationName: orgToDelete?.name ?? "",
							})}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<p className="text-sm font-semibold text-error">
							{t("selectOrganization.deleteDialog.irreversibleWarning")}
						</p>
						<div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-warning-content">
							{t("selectOrganization.deleteDialog.dataLossWarning")}
						</div>
						<div className="space-y-2">
							<Label htmlFor="confirm-org-name">
								{t("selectOrganization.deleteDialog.confirmLabel", {
									organizationName: orgToDelete?.name ?? "",
								})}
							</Label>
							<Input
								id="confirm-org-name"
								value={deleteOrgNameConfirm}
								onChange={(e) => setDeleteOrgNameConfirm(e.target.value)}
								autoComplete="off"
								data-testid="delete-organization-confirm-input"
							/>
						</div>
					</div>
					<DialogFooter>
						<button
							type="button"
							className="btn btn-outline"
							onClick={handleCloseDeleteDialog}
							disabled={deleteOrganizationMutation.isPending}
						>
							{t("common.cancel")}
						</button>
						<button
							type="button"
							className="btn btn-error text-error-content"
							onClick={handleConfirmDeleteOrganization}
							disabled={
								!orgToDelete ||
								deleteOrgNameConfirm.trim() !== (orgToDelete?.name ?? "") ||
								deleteOrganizationMutation.isPending
							}
							data-testid="delete-organization-confirm-button"
						>
							{deleteOrganizationMutation.isPending
								? t("selectOrganization.deleteDialog.deleting")
								: t("selectOrganization.deleteDialog.confirmButton")}
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</AppBaseLayout>
	);
}

function Section({
	title,
	items,
	emptyText,
	testId,
	onRequestDelete,
}: {
	title: string;
	items: API_UserOrganization[];
	emptyText: string;
	testId?: string;
	onRequestDelete?: (org: API_UserOrganization) => void;
}) {
	const { t } = useTranslation();
	return (
		<div className="space-y-3 sm:space-y-4" data-testid={testId}>
			<div className="flex items-center gap-2">
				<h2 className="text-lg sm:text-xl font-semibold text-base-content flex items-center gap-2">
					<ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
					{title}
				</h2>
			</div>
			{items.length === 0 ? (
				<div className="rounded-lg border border-dashed p-4 sm:p-6 text-center text-sm sm:text-base text-base-content/70">
					{emptyText}
				</div>
			) : (
				<div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
					{items.map((org) => (
						<Link
							key={org.id}
							to="/app/org/$orgId"
							params={{ orgId: org.id }}
							className="cursor-pointer"
						>
							<Card
								data-testid="organization-card"
								className="h-full hover:shadow-lg transition-shadow duration-200 border-2 hover:border-primary/50 flex flex-col justify-between break-words"
							>
								<CardHeader className="p-4 sm:p-6">
									<div className="flex items-center justify-between gap-2">
										<div className="flex items-center gap-2">
											<Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
											<Badge
												data-testid="organization-role"
												variant="secondary"
												className="text-xs"
											>
												{getOrganizationRoleLabel(org.role as Role, t)}
											</Badge>
										</div>
										{org.role === Role.ORG_OWNER && onRequestDelete && (
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="text-error hover:text-error hover:bg-error/10"
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													onRequestDelete(org);
												}}
												data-testid="delete-organization-button"
												aria-label={t("selectOrganization.deleteButton")}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										)}
									</div>
									<CardTitle
										data-testid="organization-card-title"
										className="flex items-center justify-between text-base sm:text-lg break-words"
									>
										<span
											data-testid="organization-name"
											className="flex items-center gap-2 break-words line-clamp-3"
										>
											{org.name}
										</span>
									</CardTitle>
								</CardHeader>
								<CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
									<Button
										data-testid="select-organization-button"
										className="w-full text-primary-content"
									>
										{t("selectOrganization.selectButton")}
									</Button>
								</CardContent>
							</Card>
						</Link>
					))}
				</div>
			)}
		</div>
	);
}
